import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type CallDirection = 'inbound' | 'outbound'

export type CallStatus =
  | 'completed'
  | 'no-answer'
  | 'no_answer'
  | 'busy'
  | 'failed'
  | 'missed'
  | 'canceled'
  | 'ringing'
  | 'in-progress'
  | string

export type CallPeriod = 'all' | 'today' | 'week' | 'month' | 'custom'

export type AnswerType = 'phone' | 'web' | 'ai' | 'voicemail' | null

export interface CompanyCallLog {
  id: number
  call_sid: string
  from_number: string
  to_number: string
  status: CallStatus
  direction: CallDirection
  answer_type: AnswerType
  answer_type_display: string | null
  answered_by_agent: boolean
  final_destination: string | null
  display_destination: string | null
  duration: number | null
  duration_formatted: string
  started_at: string
  updated_at: string
  agent_name: string | null
  contact_name: string | null
  contact_avatar_url: string | null
  has_recordings: boolean
  recordings_count: number
  has_voicemail: boolean
  is_internal: boolean
  target_agent_name: string | null
  initiator_agent_name: string | null
  internal_peer_name?: string | null
  ai_summary: string | null
  /**
   * Point d'écoute d'un appel pris par un agent IA, quel que soit le
   * pipeline qui a produit l'audio (capture legacy ou enregistrement
   * Twilio). Chemin relatif SANS jeton : à compléter avec ?token= avant de
   * le donner à un lecteur audio.
   */
  ai_audio_url: string | null
  /** Closed to this person (no audio, no transcript): shown like a call without a recording. */
  recording_locked?: boolean
  call_source: string | null
  pending_tasks_count: number
}

export interface CompanyCallLogsResponse {
  results: CompanyCallLog[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface CompanyCallLogsFilters {
  period?: CallPeriod
  status?: string
  direction?: 'all' | CallDirection
  /** 'ai' = uniquement les appels pris en charge par un agent IA. */
  answeredBy?: 'all' | 'ai'
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export type RecordingStatus = 'processing' | 'available' | 'deleted'

export interface CallRecording {
  id: number
  recording_sid: string
  duration_sec: number | null
  status: RecordingStatus
  created_at: string
  available_at: string | null
  /** URL proxy Django avec token JWT en query param (playable directement). */
  audio_url: string | null
}

/** GET /api/companies/{id}/logs/{callId}/recordings/ — enregistrements d'un appel. */
export function useCallRecordings(
  companyId: number | undefined,
  callId: number | null,
) {
  return useQuery({
    queryKey: ['call-recordings', companyId, callId],
    queryFn: async () => {
      const { data } = await api.get<CallRecording[]>(
        `/api/companies/${companyId}/logs/${callId}/recordings/`,
      )
      return data
    },
    enabled: !!companyId && !!callId,
    staleTime: 10_000,
  })
}

/** DELETE /api/companies/{id}/logs/{callId}/recordings/{recordingId}/ — supprime un enregistrement. */
export function useDeleteCallRecording(
  companyId: number | undefined,
  callId: number,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (recordingId: number) => {
      await api.delete(
        `/api/companies/${companyId}/logs/${callId}/recordings/${recordingId}/`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['call-recordings', companyId, callId],
      })
      queryClient.invalidateQueries({
        queryKey: ['company-call-logs', companyId],
      })
    },
  })
}

/** GET /api/companies/{id}/logs/ — journaux d'appels paginés et filtrés. */
export function useCompanyCallLogs(
  companyId: number | undefined,
  filters: CompanyCallLogsFilters,
) {
  return useQuery({
    queryKey: ['company-call-logs', companyId, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.period && filters.period !== 'all') {
        params.set('period', filters.period)
      }
      if (filters.status && filters.status !== 'all') {
        params.set('status', filters.status)
      }
      if (filters.direction && filters.direction !== 'all') {
        params.set('direction', filters.direction)
      }
      if (filters.answeredBy && filters.answeredBy !== 'all') {
        params.set('answered_by', filters.answeredBy)
      }
      if (filters.startDate) params.set('start_date', filters.startDate)
      if (filters.endDate) params.set('end_date', filters.endDate)
      if (filters.page) params.set('page', String(filters.page))
      if (filters.pageSize) params.set('page_size', String(filters.pageSize))

      const qs = params.toString()
      const url = `/api/companies/${companyId}/logs/${qs ? `?${qs}` : ''}`
      const { data } = await api.get<CompanyCallLogsResponse>(url)
      return data
    },
    enabled: !!companyId,
    staleTime: 10_000,
  })
}
